import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpOperation } from '../../enums/http-operation';
import { EndpointService } from '../../services/endpoints/endpoint.service';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpErrorService } from '../../services/http-error.service';
import * as fromInvoiceActions from './invoice-document.actions';
import { InvoiceDocumentDetailModel } from '../../models/invoice/invoice-document-detail.model';
import { InvoiceDocumentOverviewModel } from '../../models/invoice/invoice-document-overview.model';

@Injectable()
export class InvoiceDocumentEffects {
  fetchInvoiceDocumentOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvoiceActions.fetchInvoiceDocumentOverview),
      switchMap(() => {
        const endpoint = this.endpointService.getInvoiceDocumentOverviewSlug(HttpOperation.Get);

        return this.http.get<unknown>(endpoint).pipe(
          map((json: unknown[]) => {
            const invoices = plainToInstance(InvoiceDocumentOverviewModel, json);
            return fromInvoiceActions.setInvoiceDocumentOverview({ payload: invoices });
          }),
          catchError((error) =>
            this.httpErrorService.handleError(
              'Invoice',
              fromInvoiceActions.fetchInvoiceDocumentOverview,
              fromInvoiceActions,
              error,
            ),
          ),
        );
      }),
    ),
  );

  //* **** Invoice Document Detail
  fetchInvoiceDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvoiceActions.fetchInvoiceDocuments),
      map((action) => action.payload),
      switchMap((isFilter) => {
        const endpoint = this.endpointService.getInvoiceDocumentSlug(HttpOperation.Get);

        // Optional filter
        let params = new HttpParams();
        params = isFilter ? params.append('is_template', isFilter) : params;

        return this.http.get<unknown>(endpoint, { params }).pipe(
          map((json: unknown[]) => {
            const invoices = plainToInstance(InvoiceDocumentDetailModel, json);
            return fromInvoiceActions.setInvoiceDocuments({ payload: invoices });
          }),
          catchError((error) =>
            this.httpErrorService.handleError(
              'Invoice',
              fromInvoiceActions.fetchInvoiceDocuments,
              fromInvoiceActions,
              error,
            ),
          ),
        );
      }),
    ),
  );

  fetchSingleInvoiceDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvoiceActions.fetchSingleInvoiceDocument),
      map((action) => action.payload),
      switchMap((fileId) => {
        const endpoint = this.endpointService.getInvoiceDocumentSlug(HttpOperation.Get, fileId);

        return this.http.get<unknown>(endpoint).pipe(
          map((json: unknown) => {
            const existingInvoice = plainToInstance(InvoiceDocumentDetailModel, json);
            return fromInvoiceActions.setSingleInvoiceDocument({ payload: existingInvoice });
          }),
          catchError((error) =>
            this.httpErrorService.handleError(
              'Invoice',
              fromInvoiceActions.fetchSingleInvoiceDocument,
              fromInvoiceActions,
              error,
            ),
          ),
        );
      }),
    ),
  );

  generateInvoiceDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvoiceActions.generateInvoiceDocument),
      map((action) => action.payload),
      switchMap((payload: InvoiceDocumentDetailModel) => {
        const endpoint = this.endpointService.getInvoiceDocumentSlug(HttpOperation.Post);
        const postModel = instanceToPlain(payload);

        return this.http.post<unknown>(endpoint, postModel).pipe(
          map((generatedFile: any) => {
            const newGeneratedFile = plainToInstance(InvoiceDocumentDetailModel, generatedFile);
            return fromInvoiceActions.invoiceDocumentGenerated({ payload: newGeneratedFile });
          }),
          catchError((error) =>
            this.httpErrorService.handleError(
              'Invoice',
              fromInvoiceActions.generateInvoiceDocument,
              fromInvoiceActions,
              error,
            ),
          ),
        );
      }),
    ),
  );

  updateInvoiceDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvoiceActions.updateInvoiceDocument),
      map((action) => action.payload),
      switchMap((invoiceDocument) => {
        const endpoint = this.endpointService.getInvoiceDocumentSlug(
          HttpOperation.Delete,
          invoiceDocument.id,
        );

        const invoiceDocumentHttp = instanceToPlain(invoiceDocument);
        return this.http.put(endpoint, invoiceDocumentHttp).pipe(
          map((updatedInvoice) => {
            const updatedInvoiceDocument = plainToInstance(
              InvoiceDocumentDetailModel,
              updatedInvoice,
            );
            return fromInvoiceActions.invoiceDocumentUpdated({ payload: updatedInvoiceDocument });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Invoice',
              fromInvoiceActions.updateInvoiceDocument,
              fromInvoiceActions,
              error,
            );
          }),
        );
      }),
    ),
  );

  deleteInvoiceDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromInvoiceActions.deleteInvoiceDocument),
      map((action) => action.payload),
      switchMap((invoiceDocument) => {
        const endpoint = this.endpointService.getInvoiceDocumentSlug(
          HttpOperation.Delete,
          invoiceDocument.id,
        );

        return this.http.delete(endpoint, { body: { id: invoiceDocument.id } }).pipe(
          map(() => fromInvoiceActions.invoiceDocumentDeleted({ payload: invoiceDocument })),
          catchError((error) =>
            this.httpErrorService.handleError(
              'Invoice',
              fromInvoiceActions.deleteInvoiceDocument,
              fromInvoiceActions,
              error,
            ),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
  ) {}
}
